/**
 * Toast notifications (Sonner-style).
 *
 * Usage:
 *   import { useToast } from "@/shared/ui/Toast";
 *   const toast = useToast();
 *   toast.success("Person created!");
 *   toast.error("Something went wrong", { duration: 8000 });
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { Check, CircleAlert, Info, X } from "lucide-react";

import { cn } from "@/shared/lib/cn";

export const DEFAULT_TOAST_DURATION_MS = 4000;
const TOAST_EXIT_MS = 200;

export type ToastPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

const TOAST_POSITION_CLASS: Record<ToastPosition, string> = {
  "top-right": "top-4 right-4 sm:top-6 sm:right-6",
  "top-left": "top-4 left-4 sm:top-6 sm:left-6",
  "bottom-right": "bottom-4 right-4 sm:bottom-6 sm:right-6",
  "bottom-left": "bottom-4 left-4 sm:bottom-6 sm:left-6",
};

export interface ToastOptions {
  /** Auto-dismiss delay in ms. Ignored when `persist` is true. */
  duration?: number;
  persist?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastRecord {
  id: string;
  message: string;
  type: "success" | "error" | "info";
  durationMs: number;
  persist?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastContextType {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType>({
  success: () => {},
  error: () => {},
  info: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

const TYPE_STYLES = {
  success: {
    icon: Check,
    iconWrap: "bg-success/12 text-success",
    progress: "bg-success/50",
  },
  error: {
    icon: CircleAlert,
    iconWrap: "bg-danger/12 text-danger",
    progress: "bg-danger/50",
  },
  info: {
    icon: Info,
    iconWrap: "bg-primary/10 text-primary",
    progress: "bg-primary/40",
  },
} as const;

function ToastItem({
  toast,
  onRemove,
}: {
  toast: ToastRecord;
  onRemove: (id: string) => void;
}) {
  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);
  const deadlineRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const styles = TYPE_STYLES[toast.type];
  const Icon = styles.icon;
  const durationMs = toast.durationMs;

  const dismiss = useCallback(() => {
    setExiting(true);
    window.setTimeout(() => onRemove(toast.id), TOAST_EXIT_MS);
  }, [onRemove, toast.id]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleDismiss = useCallback(() => {
    if (toast.persist) return;
    clearTimer();
    const remaining = deadlineRef.current - Date.now();
    if (remaining <= 0) {
      dismiss();
      return;
    }
    timerRef.current = setTimeout(dismiss, remaining);
  }, [dismiss, toast.persist]);

  useEffect(() => {
    if (toast.persist) return;
    deadlineRef.current = Date.now() + durationMs;
    scheduleDismiss();
    return clearTimer;
  }, [durationMs, scheduleDismiss, toast.persist]);

  const handleAction = () => {
    toast.onAction?.();
    dismiss();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={() => {
        setPaused(true);
        clearTimer();
      }}
      onMouseLeave={() => {
        setPaused(false);
        scheduleDismiss();
      }}
      className={cn(
        "tnn-toast group pointer-events-auto relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-lg",
        exiting && "tnn-toast--out",
      )}
    >
      <div className="flex items-start gap-3 px-4 py-3.5 pr-10">
        <span
          className={cn(
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
            styles.iconWrap,
          )}
          aria-hidden
        >
          <Icon className="size-3.5 stroke-[2.25]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-snug text-foreground">{toast.message}</p>
          {toast.actionLabel && toast.onAction ? (
            <button
              type="button"
              className="mt-2 text-xs font-medium text-primary transition-colors hover:text-primary/80"
              onClick={handleAction}
            >
              {toast.actionLabel}
            </button>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={dismiss}
          className={cn(
            "absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-opacity",
            "opacity-0 hover:bg-muted hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <X className="size-3.5" />
        </button>
      </div>
      {!toast.persist ? (
        <div className="h-0.5 w-full bg-border/30" aria-hidden>
          <div
            className={cn("tnn-toast-progress h-full w-full", styles.progress)}
            style={
              {
                "--toast-duration": `${durationMs}ms`,
                animationPlayState: paused ? "paused" : "running",
              } as CSSProperties
            }
          />
        </div>
      ) : null}
    </div>
  );
}

export function ToastProvider({
  children,
  position = "top-right",
  duration = DEFAULT_TOAST_DURATION_MS,
}: {
  children: ReactNode;
  /** Viewport anchor for the toast stack; override per app shell when needed. */
  position?: ToastPosition;
  /** Default auto-dismiss delay when a toast omits `duration` in its options. */
  duration?: number;
}) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: "success" | "error" | "info", options?: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const durationMs = options?.duration ?? duration;
      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          type,
          durationMs: Math.max(0, durationMs),
          persist: options?.persist,
          actionLabel: options?.actionLabel,
          onAction: options?.onAction,
        },
      ]);
    },
    [duration],
  );

  const ctx: ToastContextType = {
    success: (msg, options) => addToast(msg, "success", options),
    error: (msg, options) => addToast(msg, "error", options),
    info: (msg, options) => addToast(msg, "info", options),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div
        aria-label="Notifications"
        className={cn(
          "pointer-events-none fixed z-[100] flex max-h-[min(100vh-2rem,32rem)] flex-col gap-2",
          TOAST_POSITION_CLASS[position],
        )}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
